if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form.form-product') || this.querySelector('form');
      if (!this.form) return;
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.section = this.closest('.shopify-section');
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.isCbb = this.dataset.cbb === 'true';
      this.form.addEventListener('submit', event => this.onSubmit(event));
      if (this.cart) this.submitButton?.setAttribute('aria-haspopup', 'dialog');
      if (this.isCbb) {
        this.form.querySelector('[name="id"]').disabled = true;
        if (this.submitButton) this.submitButton.disabled = true;
      } else {
        // Native selectors update this ID when an available option is chosen.
        const input = this.form.querySelector('[name="id"]');
        const picker = this.section?.querySelector('variant-selects');
        if (input) input.disabled = Boolean(picker && !picker.hasConfirmedSelection?.());
      }
      const optionsBaseReady = !this.isCbb && !this.form.querySelector('[name="id"]')?.disabled && !(this.fpOptionsInitialDisabled ?? this.submitButton?.disabled);
      if (this.section?.querySelector('[data-fp-option-group], [data-fp-option-property]')) {
        this.dataset.fpBaseReady = String(optionsBaseReady);
        if (!window.FPProductOptions) {
          if (this.submitButton) this.submitButton.disabled = true;
          this.form.querySelectorAll('[data-fp-dynamic-checkout]').forEach(checkout => { checkout.inert = true; checkout.style.visibility = 'hidden'; });
        }
      }
      window.FPProductOptions?.gate(this.section, optionsBaseReady);
    }

    handleErrorMessage(message = '') {
      const wrapper = this.querySelector('.product-form__error-message-wrapper');
      if (!wrapper) return;
      wrapper.hidden = !message;
      const text = wrapper.querySelector('.product-form__error-message');
      if (text) text.textContent = message;
    }

    capture() {
      if (this.section?.querySelector('[data-fp-option-group], [data-fp-option-property]') && !window.FPProductOptions) throw new Error('Aguarde o carregamento das opções do produto.');
      window.FPProductOptions?.guard(this.section);
      const data = new FormData(this.form);
      const input = this.form.querySelector('[name="id"]');
      if (!input || input.disabled || !/^\d+$/.test(input.value) || Number(input.value) <= 0) throw new Error('Selecione uma opção disponível.');
      if (this.isCbb) {
        const state = this.section?.fpCbbState;
        const record = state?.record;
        const quantity = Number(data.get('quantity') || 1);
        const width = Number(this.section.querySelector('#select-width')?.value);
        const height = Number(this.section.querySelector('#select-height')?.value);
        if (state?.state !== 'ready' || !record?.available || record.id !== input.value || record.requestedWidth !== width || record.requestedHeight !== height || record.quantity !== quantity) throw new Error('Aguarde a confirmação das medidas.');
        const rule = record.quantityRule;
        if (!Number.isInteger(quantity) || quantity < rule.min || (rule.max !== null && quantity > rule.max) || quantity % rule.increment !== 0) throw new Error('Ajuste a quantidade aos limites do produto.');
        const rope = this.section.querySelector('[name="lado-corda"]:checked');
        if (record.properties['Lado da cordinha'] && record.properties['Lado da cordinha'] !== rope?.value) throw new Error('Confirme o lado da cordinha.');
        for (const [name, value] of Object.entries(record.properties)) data.set('properties[' + name + ']', value);
        data.set('id', record.id); data.set('quantity', quantity);
      } else {
        const picker = this.section?.querySelector('variant-selects');
        if (picker && (!picker.hasConfirmedSelection?.() ||
          String(picker.currentVariant?.id) !== input.value)) {
          throw new Error('Aguarde a confirmação da opção selecionada.');
        }
      }
      window.FPProductOptions?.guard(this.section, data);
      if (this.cart) {
        data.append('sections', this.cart.getSectionsToRender().map(section => section.id));
        data.append('sections_url', location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      return data;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.inFlight || this.cartUncertain || this.submitButton?.disabled || this.submitButton?.getAttribute('aria-disabled') === 'true') return;
      let payload;
      try { payload = this.capture(); } catch (error) { this.handleErrorMessage(error.message); return; }
      this.inFlight = true;
      window.FPProductOptions?.gate(this.section);
      this.handleErrorMessage();
      this.submitButton?.setAttribute('aria-disabled', 'true');
      this.submitButton?.classList.add('loading');
      const spinner = this.querySelector('.loading__spinner'); spinner?.classList.remove('hidden');
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      let received = false;
      let sent = false;
      const record = this.isCbb ? this.section?.fpCbbState?.record : null;
      try {
        if (record?.grid) {
          const selector = this.section.querySelector('variant-width-height');
          const quote = await selector?.source?.cartPricegrid(record, record.requestedWidth, record.requestedHeight, controller.signal, true);
          if (!quote || ['price', 'cost', 'width', 'height', 'discountPercent'].some(key => quote[key] !== record.grid[key])) {
            selector?.setState('error', null, 'O preço da grade mudou ou não pôde ser confirmado. Selecione as medidas novamente.');
            throw new Error('O preço da grade mudou ou não pôde ser confirmado. Selecione as medidas novamente.');
          }
          if (this.section.fpCbbState.record !== record) throw new Error('A seleção mudou. Confirme as medidas novamente.');
          payload = this.capture();
          if (payload.get('selling_plan') || ['_cart_transform', '_motor_id', '_motor_price', '_cassete_id', '_cassete_price', '_nodrills_id', '_nodrills_price', '_cardboard_box_id'].some(key => payload.get(`properties[${key}]`))) throw new Error('Esta combinação de opções não permite confirmar o preço da grade.');
        }
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        sent = true;
        const response = await fetch(routes.cart_add_url, { ...config, body: payload, signal: controller.signal });
        const data = await response.json();
        if (!response.ok || data.status) {
          received = response.status >= 400 && response.status < 500;
          const message = data.description || data.message || 'Não foi possível adicionar o produto.';
          publish(PUB_SUB_EVENTS.cartError, { source: 'product-form', productVariantId: payload.get('id'), errors: data.errors || message, message });
          throw new Error(message);
        }
        const item = Array.isArray(data.items) ? data.items[0] : data;
        if (String(item?.variant_id || item?.id) !== String(payload.get('id'))) throw new Error('O carrinho retornou uma variante diferente. Confira o carrinho antes de continuar.');
        received = true;
        const gridMismatch = record?.grid && item.original_price !== record.price;
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: payload.get('id'), cartData: data });
        if (this.cart) {
          this.cart.renderContents(data);
          this.cart.classList.remove('is-empty');
        } else {
          window.location.assign(routes.cart_url);
        }
        if (gridMismatch) {
          this.cartUncertain = true;
          const message = 'O Shopify retornou um preço diferente da grade. Confira o carrinho antes de continuar; o produto não será enviado novamente.';
          this.handleErrorMessage(message);
          const cartError = this.cart?.querySelector?.('#CartDrawer-CartErrors');
          if (cartError) cartError.textContent = message;
        }
      } catch (error) {
        if (!received && sent) {
          // A timed-out add may have succeeded. Never repeat it automatically.
          this.cartUncertain = true;
          this.handleErrorMessage('Não foi possível confirmar a inclusão. Confira o carrinho antes de tentar novamente. ' + (error.name === 'AbortError' ? '' : error.message));
          if (!this.querySelector('[data-cart-recovery]')) {
            const link = document.createElement('a'); link.href = routes.cart_url;
            link.dataset.cartRecovery = ''; link.textContent = 'Conferir carrinho';
            this.querySelector('.product-form__error-message-wrapper')?.append(link);
          }
        } else this.handleErrorMessage(error.message);
      } finally {
        clearTimeout(timeout); this.inFlight = false;
        this.submitButton?.classList.remove('loading'); spinner?.classList.add('hidden');
        if (!this.cartUncertain) this.submitButton?.removeAttribute('aria-disabled');
        window.FPProductOptions?.gate(this.section);
      }
    }
  });
}
